/**
 * Displays a select control to apply tag values against a group of assets in the inventory table.
 * The default option is to 'keep as is' which will revert any changes made to the group.
 * Any other valid values selected will be applied to the group.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { inject, observer } from 'mobx-react';
import { IReactComponent } from 'mobx-react/dist/types/IReactComponent';
import { TARGET_ACTION } from '@kentik/ui-shared/labels/lib';
import pluralize from '@kentik/ui-shared/util/pluralize';
import { AssetTagsStore } from 'app/stores/assetTags/$assetTags';
import { Flex, Select, Tooltip, Tag, Text } from 'core/components';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';

interface Props {
  $assetTags: AssetTagsStore;
  collection: AssetTagInventoryCollection;
  tagId: string;
}

function TargetValueSelector({ $assetTags, collection, tagId }: Props) {
  const initialSelectedValue = collection.initTargetValue({ tagId });
  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);

  useEffect(() => {
    setSelectedValue(initialSelectedValue);
  }, [initialSelectedValue]);

  /**
   * Renders the value for a given tag id, optionally indicating the number of errors in the group
   * This is used in both the actual value renderer and the select's placeholder.
   * We use this on the placeholder because that's what's rendered in default state with nothing fetched yet.
   * This ensures consistent labeling and error state.
   *
   * Single mode errors won't use the tag count badge, but will instead just render the label in red with a general tooltip.
   */
  const valueRenderer = useCallback(
    (tagIdToRender) => {
      const tagErrorCount = collection.getTagErrorCount(tagIdToRender);
      const hasErrors = tagErrorCount > 0;
      const label = selectedValue === TARGET_ACTION.KEEP.value ? TARGET_ACTION.KEEP.label : selectedValue;
      const tooltip = collection.singleMode
        ? 'A selection is required'
        : `${tagErrorCount} ${pluralize(collection.assetLabel, tagErrorCount).toLowerCase()} require${tagErrorCount === 1 ? 's' : ''} a selection`;

      return (
        <Tooltip content={tooltip} disabled={!hasErrors} position="top">
          <Flex alignItems="center" gap={1}>
            {hasErrors && !collection.singleMode && (
              <Tag intent="danger" round>
                {tagErrorCount}
              </Tag>
            )}
            <Text color={hasErrors && collection.singleMode ? 'danger' : undefined}>{label}</Text>
          </Flex>
        </Tooltip>
      );
    },
    [collection, selectedValue]
  );

  if (!$assetTags.canUpdate) {
    return valueRenderer(tagId);
  }

  /**
   * Defines the fetch routine when expanding the selector.
   * We only fetch on the first expansion, then cache in the collection by tag group.
   */
  const onQuery = () => collection.fetchTagValues({ tagId });

  /**
   * When selecting a new value, we update all the values in the group.
   */
  const onChange = (value: string) => {
    collection.updateGroupValues({ value, tagId });
    setSelectedValue(value);
  };

  return (
    <Select
      placeholder={valueRenderer(tagId)}
      valueRenderer={() => valueRenderer(tagId)}
      options={[]}
      onQuery={onQuery}
      exactMatch={false}
      onChange={onChange}
      values={[selectedValue]}
      renderAsButton
      style={{ flex: 1 }}
      fill
    />
  );
}

export default inject('$assetTags')(observer(TargetValueSelector as IReactComponent));
